import type { ReactNode } from 'react';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import { Box, Container, Grid, Stack, Typography, styled } from '@mui/material';
import { fontWeights } from '@/@mui/tokens/font-weights';
import {
  courseTypeIconMap,
  modalityIconMap,
} from '@/constants/resources/resources-icons';
import type { CourseDetails } from '@/data/catalog/course';
import { OfferCard } from '@/packages/courses/pages/details/components/offer-card';
import { formatDuration } from '@/utils/masks/time';

const StyledHeader = styled(Box)(({ theme }) => ({
  background: theme.palette.primary.gradient,
  marginTop: 56,
  position: 'relative',
  padding: theme.spacing(6, 0),
}));

interface InfoCardProps {
  label: string;
  value: string;
  icon?: ReactNode;
}

const InfoCard = ({ label, value, icon }: InfoCardProps) => {
  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={2}
      bgcolor="white"
      p={1}
      width={{ md: 200 }}
      borderRadius={1}
    >
      {icon && (
        <Box
          width={40}
          height={40}
          display="flex"
          alignItems="center"
          justifyContent="center"
          bgcolor="primary.light"
          borderRadius={1}
        >
          {icon}
        </Box>
      )}
      <Box>
        <Typography fontWeight={fontWeights.fontWeightSemibold}>
          {label}
        </Typography>
        <Typography>{value}</Typography>
      </Box>
    </Stack>
  );
};

export const DetailsHeader = ({ course, institution }: CourseDetails) => {
  const courseTypeIcon =
    course?.course_type?.type && courseTypeIconMap[course?.course_type?.type];
  const modalityIcon =
    course?.modality?.type && modalityIconMap[course?.modality?.type];

  return (
    <StyledHeader height={{ xs: 'auto', md: '500px' }}>
      <Container maxWidth="lg">
        <Grid
          container
          spacing={{ xs: 2, sm: 4 }}
          height={{ xs: 'auto', md: '500px' }}
        >
          <Grid size={{ xs: 12, md: 8 }} mt={{ md: 12 }}>
            <Typography color="white">{institution?.name}</Typography>
            <Typography color="white" variant="h1" mb={4} mt={1}>
              {course?.name}
            </Typography>
            <Stack direction={{ md: 'row' }} gap={2}>
              {course?.course_type?.label && (
                <InfoCard
                  label="Categoria"
                  value={course?.course_type?.label || ''}
                  icon={courseTypeIcon}
                />
              )}
              {course?.modality?.label && (
                <InfoCard
                  label="Modalidade"
                  value={course?.modality?.label || ''}
                  icon={modalityIcon}
                />
              )}
              {course?.duration?.type && (
                <InfoCard
                  label="Duração"
                  value={formatDuration(
                    course?.duration?.type,
                    course?.duration?.quantity || 0
                  )}
                  icon={<AccessTimeRoundedIcon fontSize="small" />}
                />
              )}
            </Stack>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <OfferCard course={course} institution={institution} />
          </Grid>
        </Grid>
      </Container>
    </StyledHeader>
  );
};
